import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Shield, Clock, Star, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1614526502706-33b98ed74aa3?crop=entropy&cs=srgb&fm=jpg&q=85";

const features = [
  {
    icon: Sparkles,
    title: "Premium Kwaliteit",
    description: "Wij gebruiken alleen de beste producten voor een showroom-finish.",
  },
  {
    icon: Shield,
    title: "100% Garantie",
    description: "Niet tevreden? Wij lossen het kosteloos op.",
  },
  {
    icon: Clock,
    title: "Flexibele Tijden",
    description: "Afspraken op tijden die bij u passen.",
  },
  {
    icon: Star,
    title: "Ervaren Specialists",
    description: "Jarenlange ervaring met alle merken en modellen.",
  },
];

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed database first
        await axios.post(`${API}/seed`);
        // Then fetch services
        const response = await axios.get(`${API}/services`);
        setServices(response.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ug-black/60 via-ug-black/70 to-ug-black" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          <div className="space-y-8">
            <div className="inline-block">
              <span className="px-4 py-2 text-xs font-bold tracking-widest uppercase text-primary border border-primary/30 bg-primary/10">
                Premium Auto Detailing
              </span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-black uppercase tracking-tighter text-white">
              Driven by{" "}
              <span className="text-gradient">Detail</span>
            </h1>

            <p className="text-xl sm:text-2xl text-gray-300 max-w-2xl mx-auto font-light">
              Powered by Passion
            </p>

            <p className="text-gray-400 max-w-xl mx-auto">
              Uw auto verdient de beste verzorging. Van interieur tot exterieur, wij zorgen voor een onberispelijke afwerking.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Link to="/boeken">
                <Button
                  data-testid="hero-book-btn"
                  className="bg-primary hover:bg-primary-hover text-white rounded-none uppercase tracking-widest font-bold px-8 py-4 text-sm transition-colors duration-300 hover:shadow-[0_0_30px_rgba(220,38,38,0.4)]"
                >
                  Afspraak Maken
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/diensten">
                <Button
                  data-testid="hero-services-btn"
                  variant="outline"
                  className="bg-transparent border border-white/20 text-white hover:bg-white/5 hover:border-white/40 rounded-none uppercase tracking-widest font-bold px-8 py-4 text-sm transition-colors duration-300"
                >
                  Onze Diensten
                </Button>
              </Link>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 scroll-indicator">
            <ChevronDown className="w-8 h-8 text-white/50" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section data-testid="features-section" className="py-24 bg-ug-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="group p-8 bg-ug-paper border border-white/5 hover:border-primary/30 transition-colors duration-500"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <feature.icon className="w-10 h-10 text-primary mb-6" />
                <h3 className="text-lg font-heading font-bold uppercase tracking-wide text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section data-testid="services-preview" className="py-24 bg-ug-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-primary text-sm font-bold tracking-widest uppercase">
              Onze Diensten
            </span>
            <h2 className="text-4xl sm:text-5xl font-heading font-black uppercase tracking-tighter text-white mt-4">
              Wat Wij Bieden
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={service.id}
                  data-testid={`service-card-${index}`}
                  className="group relative overflow-hidden bg-ug-black border border-white/5 hover:border-primary/30 transition-colors duration-500"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={service.image_url}
                      alt={service.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex justify-between items-start">
                      <h3 className="text-xl font-heading font-bold uppercase tracking-wide text-white">
                        {service.name}
                      </h3>
                      <span className="text-primary font-bold">
                        va €{service.price.toFixed(0)}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm line-clamp-2">
                      {service.description}
                    </p>
                    <Link
                      to="/diensten"
                      className="inline-flex items-center text-sm font-medium text-primary hover:text-white transition-colors duration-300"
                    >
                      Meer Info
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/diensten">
              <Button
                data-testid="view-all-services-btn"
                variant="outline"
                className="bg-transparent border border-white/20 text-white hover:bg-white/5 hover:border-white/40 rounded-none uppercase tracking-widest font-bold px-8 py-4"
              >
                Alle Diensten Bekijken
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 bg-ug-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(https://images.unsplash.com/photo-1592713864248-fdef16231790?crop=entropy&cs=srgb&fm=jpg&q=85)`,
            }}
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-heading font-black uppercase tracking-tighter text-white mb-6">
            Klaar voor een <span className="text-primary">Transformatie</span>?
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
            Vraag vandaag nog een vrijblijvende offerte aan of boek direct een afspraak.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/offerte">
              <Button
                data-testid="cta-quote-btn"
                className="bg-primary hover:bg-primary-hover text-white rounded-none uppercase tracking-widest font-bold px-8 py-4 transition-colors duration-300 hover:shadow-[0_0_30px_rgba(220,38,38,0.4)]"
              >
                Offerte Aanvragen
              </Button>
            </Link>
            <Link to="/boeken">
              <Button
                data-testid="cta-book-btn"
                variant="outline"
                className="bg-transparent border border-white/20 text-white hover:bg-white/5 hover:border-white/40 rounded-none uppercase tracking-widest font-bold px-8 py-4 transition-colors duration-300"
              >
                Direct Boeken
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
